<template>
  <v-card
    :max-width="maxWidth" :height="givenHeight" class="pa-2 elevation-0 dragAndDropCard"
    @drop.prevent="onDrop($event)" @dragover.prevent="dragover = true" @dragenter.prevent="dragover = true"
    @dragleave.prevent="dragover = false"
  >
    <div class="dropzone d-flex flex-row">
      <v-card-text>
        <v-container fluid class="fill-height">
          <v-row v-if="singleImage && loadedImgUrl" justify="center" align="center" class="imagePreviewWrapper ma-3">
            <v-img :src="loadedImgUrl" max-height="100" max-width="256" contain></v-img>
          </v-row>
          <v-row class="d-flex flex-column" justify="center" align="center" dense>
            <transition name="fade">
              <p
                v-if="errorVisible" id="drag_drop_error_msg" class="font-italic error--text text-center"
                :class="{ 'apply-shake': shakeAnimation }"
              >
                {{ errorText }}
              </p>
            </transition>

            <div class="virtual-scroll">
              <p
                v-for="(item, index) in uploadedFiles" :key="item.lastModified"
                class="subtitle-1 text-wrap text-center" :class="dragover ? 'primary--text' : 'grey--text'"
              >
                {{ uploadedFileAsText(item) }}
                <span>
                  <v-btn v-if="uploadedFiles" elevation="0" text class="error--text pa-0" @click="dumpFile(index)">
                    {{ $t('main.delete') }}
                  </v-btn>
                </span>
              </p>
            </div>

            <slot name="progress-bar"> </slot>
            <!-- UPLOAD BTN -->
            <slot name="main-action">
              <div v-if="uploadedFiles.length === 0" class="d-inline-block">
                <v-btn
                  class="pa-1 pr-2 body-2 white--text modalButton"
                  :class="!addSignatureDrawerButton && googlePickerVisible && 'splitted-button'" :loading="isSelecting"
                  @click="openFileUploadModal"
                >
                  <v-icon class="mx-1 modalButton">{{ uploadIcon }}</v-icon>
                  {{ uploadText }}
                </v-btn>
                <v-btn
                  v-if="addSignatureDrawerButton" id="draw_signature"
                  class="ml-3 pa-1 pr-2 body-2 white--text modalButton" @click="openSignatureModal"
                >
                  <v-icon>mdi-pen</v-icon>
                  {{ $t('signatureDrawer.title') }}
                </v-btn>
                <google-picker
                  v-if="googlePickerVisible"
                  :class="addSignatureDrawerButton && ' ml-3 none-splitted-button'"
                  :pdf="validFormats.includes('application/pdf')"
                  :images="validFormats.includes('image/png') || validFormats.includes('image/jpeg')"
                  @picked="handleGooglePicked"
                >
                </google-picker>
              </div>
            </slot>
          </v-row>
        </v-container>
      </v-card-text>
      <input
        ref="uploader" class="d-none" type="file" :multiple="!singleImage || !singleFile" :accept="validFormats.map(x => x)"
        @change="onFileUpload($event)"
      >
    </div>
  </v-card>
</template>

<script>
import GooglePicker from './GooglePicker'
export default {
  name: 'DragDropUpload',
  components: {
    GooglePicker
  },
  props: {
    description: {
      type: String,
      default: 'Upload Files'
    },
    singleImage: {
      type: Boolean,
      default: false
    },
    singleFile: {
      type: Boolean,
      default: false
    },
    currentImageurl: {
      type: String,
      default: ''
    },
    maxWidth: {
      type: String,
      default: '436'
    },
    givenHeight: {
      type: String,
      default: '200'
    },
    validFormats: {
      type: Array,
      default(rawProps) {
        return ['application/pdf', 'image/png', 'image/jpeg']
      }
    },
    disabled: {
      type: Boolean,
      default: false
    },
    dashboardMode: {
      type: Boolean,
      default: false
    },
    addSignatureDrawerButton: {
      type: Boolean,
      default: false
    },
    uploadCaption: {
      type: String,
      default: ''
    }
  },
  emits: ['uploaded'],
  data() {
    return {
      isSelecting: false,
      dragover: false,
      uploadedFiles: [],
      loadedImgUrl: '',
      errorVisible: false,
      shakeAnimation: false,
      errorText: 'Error',
      localValidFormats: []
      // maxDocumentSize: this.$store.state.remoteConfig.maxUploadDocumentBytes,
      // maxImageSize: this.$store.state.remoteConfig.maxSignatureProfileImageBytes
    }
  },
  computed: {
    uploadIcon() {
      if (this.singleImage) return 'mdi-image-outline'
      return 'mdi-arrow-up'
    },
    uploadText() {
      if (this.uploadCaption && this.uploadCaption.length > 0) {
        return this.uploadCaption
      }
      if (this.singleImage) return this.$t('signatureProfiles.content.uploadPicture')
      return this.$t('main.uploadFile')
    },

    googlePickerVisible() {
      return this.$store.state.remoteConfig.googlePicker && this.$store.state.remoteConfig.googlePicker.enabled
    },

    getmaxDocumentSize() {
      return this.$store.state.remoteConfig.maxUploadDocumentBytes
    },
    getmaxImageSize() {
      return this.$store.state.remoteConfig.maxSignatureProfileImageBytes
    }

  },
  watch: {
    currentImageurl: function() {
      if (this.currentImageurl) {
        const splittedSigImgDataUrl = this.currentImageurl.split(';')
        if (splittedSigImgDataUrl.length > 2) {
          this.uploadedFiles[0] = new File([splittedSigImgDataUrl[2].split(',')[1]], splittedSigImgDataUrl[1].split('=')[1],
            { type: splittedSigImgDataUrl[0].split(':')[1] })
        } else {
          this.uploadedFiles[0] = new File([splittedSigImgDataUrl[1].split(',')[1]], 'Rendered image',
            { type: splittedSigImgDataUrl[0].split(':')[1] })
        }
        this.loadedImgUrl = this.currentImageurl
      }
    },
    currentFile: function() {
      if (Object.keys(this.currentFile).length !== 0) {
        this.uploadedFiles[0] = this.currentFile
      }
    }
  },
  created() {
    this.localValidFormats = [...this.validFormats]

    // Modify localValidFormats based on conditions
    if (!this.dashboardMode && !this.$options.propsData.validFormats) {
      if (this.singleImage) {
        this.localValidFormats = this.localValidFormats.filter(format => format !== 'application/pdf')
      } else {
        this.localValidFormats = this.localValidFormats.filter(format =>
          !['image/png', 'image/jpeg'].includes(format)
        )
      }
    }
  },
  mounted() {
    if (this.$store.state.currentFile) {
      this.uploadedFiles.push(this.$store.state.currentFile)
    }
    if (this.uploadedFiles.length > 0 && this.uploadedFiles[0].type.includes('image')) {
      this.loadSignatureImageFromFile()
    }
  },
  methods: {

    async onDrop(e) {
      if (this.disabled) return this.notifyParentWithError()
      this.dragover = false

      const droppedFiles = [...e.dataTransfer.files]
      let validFiles = droppedFiles.filter(item => this.localValidFormats.includes(item.type))

      if (this.singleImage || this.singleFile) {
        validFiles = validFiles.slice(0, 1) // Only allow the first file
      }

      if (validFiles.length) {
        // Validate file size
        const oversizedFile = validFiles.find(file =>
          this.singleImage ? file.size > this.getmaxImageSize : file.size > this.getmaxDocumentSize
        )

        if (oversizedFile) {
          this.showShakeyError(this.$t('dragDrop.fileTooLarge', {
            size: this.singleImage
              ? this.getmaxImageSize / 1048576
              : this.getmaxDocumentSize / 1048576
          }))
          return this.dumpCurrentFiles()
        }

        this.uploadedFiles = validFiles
        if (this.singleImage) await this.loadSignatureImageFromFile()
        this.notifyParent()
      } else {
        this.showShakeyError(this.getInvalidFormatError())
      }
    },

    dumpFile(index) {
      this.uploadedFiles.splice(index, 1)
      if (this.loadedImgUrl) this.loadedImgUrl = ''
      this.notifyParent()
    },
    dumpCurrentFiles() {
      if (this.uploadedFiles) this.uploadedFiles = []
      if (this.loadedImgUrl) this.loadedImgUrl = ''
      this.$store.commit('removeCurrentFile')
      if (this.$refs.uploader) {
        this.$refs.uploader.value = null
      }
      this.notifyParent()
    },
    async loadSignatureImageFromFile() {
      const file = this.uploadedFiles[0]
      if (!(file.size > this.getmaxImageSize)) {
        const reader = new FileReader()
        reader.onload = (event) => this.setSignatureImage(file, event.target.result)
        await reader.readAsDataURL(file)
      } else {
        //
        this.dumpCurrentFiles()
        this.showShakeyError(this.$t('dragDrop.fileTooLarge',
          { size: this.singleImage ? this.getmaxImageSize / 1048576 : this.getmaxDocumentSize / 1048576 }))
      }
    },

    setSignatureImage(file, imageDataUri) {
      const updatedDataUri = ''
      const splittedSigImgDataUrl = imageDataUri.split(';')
      this.loadedImgUrl = updatedDataUri.concat(splittedSigImgDataUrl[0], ';filename=' + file.name + ';', splittedSigImgDataUrl[1])
      this.notifyParent()
    },
    openFileUploadModal() {
      this.isSelecting = true
      window.addEventListener('focus', () => {
        this.isSelecting = false
      }, { once: true })
      this.$refs.uploader.click()
    },
    handleGooglePicked(data) {
      let files = data

      if (this.singleImage || this.singleFile) {
        files = files.slice(0, 1) // Only allow the first file
      }

      // Validate file size
      const oversizedFile = files.find(file =>
        this.singleImage ? file.size > this.getmaxImageSize : file.size > this.getmaxDocumentSize
      )

      if (oversizedFile) {
        this.showShakeyError(this.$t('dragDrop.fileTooLarge', {
          size: this.singleImage ? this.getmaxImageSize / 1048576 : this.getmaxDocumentSize / 1048576
        }))
        return this.dumpCurrentFiles()
      }

      this.uploadedFiles = files

      if (!this.isValidFormat()) {
        this.showShakeyError(this.getInvalidFormatError())
        this.dumpCurrentFiles()
      } else if (this.singleImage) {
        this.loadSignatureImageFromFile()
      }
      this.notifyParent()
    },
    onFileUpload(e) {
      let files = [...e.target.files]

      if (this.singleImage || this.singleFile) {
        files = files.slice(0, 1) // Only allow the first file
      }

      // Validate file size
      const oversizedFile = files.find(file =>
        this.singleImage ? file.size > this.getmaxImageSize : file.size > this.getmaxDocumentSize
      )

      if (oversizedFile) {
        this.showShakeyError(this.$t('dragDrop.fileTooLarge', {
          size: this.singleImage ? this.getmaxImageSize / 1048576 : this.getmaxDocumentSize / 1048576
        }))
        return this.dumpCurrentFiles()
      }

      this.uploadedFiles.push(...files)

      if (!this.isValidFormat()) {
        this.showShakeyError(this.getInvalidFormatError())
        this.dumpCurrentFiles()
      } else if (this.singleImage) {
        this.loadSignatureImageFromFile()
      }
      this.notifyParent()
    },
    notifyParent() {
      if (this.singleImage) {
        this.$emit('uploaded', this.loadedImgUrl)
      } else {
        this.$emit('uploaded', this.uploadedFiles)
      }
    },
    notifyParentWithError() {
      this.$emit('error')
    },
    showShakeyError(errText) {
      this.errorText = errText
      this.errorVisible = true
      this.shakeAnimation = true
      setTimeout(() => { this.shakeAnimation = false }, 620)
      setTimeout(() => { this.errorVisible = false }, 4000)
    },
    openSignatureModal() {
      this.$emit('onSignatureDrawerClicked')
    },
    uploadedFileAsText(file) {
      if ((file) && file.name) {
        return `${file.name} (${Math.round((file.size / 1048576) * 100) / 100} kb)`
      } else {
        return 'No filename (debug)'
      }
    },
    isValidFormat() {
      return this.uploadedFiles.every(file => {
        const fileType = file.type
        return this.localValidFormats.some(format => fileType.includes(format))
      })
    }
  }

}
</script>

<style scoped>
.imagePreviewWrapper {
  width: 10%;
  outline: 1px dashed darkgray;
}

.dropzone {
  border: 1px dashed #dfdfdf;
  border-radius: 16px;
  height: 100%;
}

@keyframes shake {

  10%,
  90% {
    transform: translate3d(-1px, 0, 0);
  }

  20%,
  80% {
    transform: translate3d(2px, 0, 0);
  }

  30%,
  50%,
  70% {
    transform: translate3d(-4px, 0, 0);
  }

  40%,
  60% {
    transform: translate3d(4px, 0, 0);
  }
}

.apply-shake {
  animation: shake 0.82s cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 420ms;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.splitted-button {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  padding-right: 7px !important;
  margin-right: 2px;
}

.virtual-scroll {
  max-height: 165px;
  width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
}

.none-splitted-button {
  border-top-left-radius: 8px !important;
  border-bottom-left-radius: 8px !important;
}
</style>
